import { emailValidation, validateCityInputValue, validateTextInputLength, validateUrl } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as yup from 'yup';

import { AUTH_ERROR_KEY, DEFAULT_DATE_FORMAT, PHONE_NUMBER_INVALID_TEXT, REQUIRED_TEXT } from './constants';
import REGEXPS from './regexp';

export const getZipCodeValidationErrorMessage = (val: string) => {
  if (isZipCodeValid(val)) return '';
  return zipCodeErrorText;
};
const isZipCodeValid = (val?: string) => (val && (val.length === 10 || val.length === 5)) || val === '';
const zipCodeErrorText = 'ZIP code isn\'t valid';
export const DATE_SEPARATOR = '/';
export const SEPARATOR_COUNT = 3;
export const MIN_YEAR_COUNT = 4;
export const WRONG_DATE_TEXT = 'Date format should be MM/DD/YYYY';
export const FUTURE_DATE_TEXT = 'Date cannot be in the future';
export const DATE_FORMAT_VALIDATOR = (val: string) => {
  if (!val) {
    return true;
  }

  const count = val.split(DATE_SEPARATOR).length;
  const isYearExist = val.split(DATE_SEPARATOR).reverse()[0].length >= MIN_YEAR_COUNT;
  const parsedDate = dayjs(val, DEFAULT_DATE_FORMAT, true);

  if (
    count === SEPARATOR_COUNT
        && isYearExist
        && parsedDate.isValid()
        && parsedDate.format(DEFAULT_DATE_FORMAT) === val
  ) {
    return true;
  }
  return false;
};
export const TODAY = dayjs().toDate();
const DATE_FORMAT = yup
  .string()
  .trim()
  .test({
    test: DATE_FORMAT_VALIDATOR as yup.TestFunction<string | undefined>,
    message: WRONG_DATE_TEXT,
  });
export default {
  CITY: yup.string().trim()
    .test(validateCityInputValue())
    .test(validateTextInputLength())
    .required(REQUIRED_TEXT),
  PHONE_NUMBER: yup.string().min(17, PHONE_NUMBER_INVALID_TEXT).required(REQUIRED_TEXT),
  REQUIRED_STRING: yup.string().required(REQUIRED_TEXT),
  STRING: yup.string().nullable(),
  URL: yup.string().test(validateUrl()),
  URL_REQUIRED: yup.string().test(validateUrl()).required(REQUIRED_TEXT),
  USER_NAME: yup.string()
    .required(AUTH_ERROR_KEY.USER_NAME_REQUIRED)
    .matches(REGEXPS.USER_NAME, AUTH_ERROR_KEY.USER_FORMAT_INVALID),
  EMAIL: emailValidation,
  EMAIL_REQUIRED: emailValidation.required(REQUIRED_TEXT),
  DATE_FORMAT,
  DATE_FORMAT_BEFORE_OTHER_DATE: (otherKey: string, text: string) =>
    yup
      .string()
      .trim()
      .test({
        exclusive: false,
        test(val) {
          const startDate = this.parent[otherKey];
          const endDate = val;
          if (startDate && endDate) {
            return dayjs(startDate).valueOf() > dayjs(endDate).valueOf();
          }
          return true;
        },
        message: text,
      })

      .test({
        test: DATE_FORMAT_VALIDATOR as yup.TestFunction<string | undefined>,
        message: WRONG_DATE_TEXT,
      }),
  DATE_FORMAT_AFTER_OTHER_DATE: (otherKey: string, text: string) =>
    yup
      .string()
      .trim()
      .test({
        exclusive: false,
        test(val) {
          if (!val) {
            return true;
          }
          const startDate = _.get(this, `parent[${otherKey}]`);

          const endDate = val;
          if (startDate && endDate) {
            return dayjs(startDate, DEFAULT_DATE_FORMAT) < dayjs(endDate, DEFAULT_DATE_FORMAT);
          }
          return true;
        },
        message: text,
      })

      .test({
        test: DATE_FORMAT_VALIDATOR as yup.TestFunction<string | undefined>,
        message: WRONG_DATE_TEXT,
      }),
  DATE_FORMAT_MAX_TODAY: yup
    .string()
    .trim()
    .test({
      test(val) {
        if (!val) {
          return true;
        }

        const currentDate = dayjs(val).toDate();
        if (currentDate <= TODAY) {
          return true;
        }
        return false;
      },
      message: FUTURE_DATE_TEXT,
    })
    .test({
      test(val) {
        if (!val) {
          return true;
        }

        const count = val.split(DATE_SEPARATOR).length;
        const isYearExist = val.split(DATE_SEPARATOR).reverse()[0].length >= MIN_YEAR_COUNT;
        if (count === SEPARATOR_COUNT && isYearExist && dayjs(val, DEFAULT_DATE_FORMAT).isValid()) {
          return true;
        }
        return false;
      },
      message: WRONG_DATE_TEXT,
    }),
  REQUIRED_POSITIVE_NUMBER_ONLY: yup
    .string()
    .required(REQUIRED_TEXT)
    .test({
      test(val) {
        if (!val || +val > 0) {
          return true;
        }
        return false;
      },
      message: 'Should be a positive number',
    }),
  ONLY_LATIN: yup.string().matches(REGEXPS.ONLY_LATIN_AND_NUMBER, 'Can only contain Latin letters'),
  REQUIRED_ARRAY: yup.array().min(1, REQUIRED_TEXT).required(REQUIRED_TEXT),
  ARRAY: yup.array().nullable(),

  REQUIRED_WITH_CUSTOM_MESSAGE: (message?: string) => yup
    .string()
    .required(message || REQUIRED_TEXT),

  ZIP_CODE: yup
    .string()
    .trim()
    .test(
      'len',
      zipCodeErrorText,
      isZipCodeValid,
    ),

  PASSWORD: yup.string().required(AUTH_ERROR_KEY.PASSWORD_IS_REQUIRED)
    .min(8, AUTH_ERROR_KEY.PASSWORD_TOO_SHORT)
    .matches(REGEXPS.UPPER_AND_LOWERCASE, AUTH_ERROR_KEY.MIXTURE_UPPER_AND_LOWER_CASE)
    .matches(REGEXPS.ONE_NUMBER_ONLY, AUTH_ERROR_KEY.MIXTURE_LETTERS_AND_NUMBERS)
    .matches(REGEXPS.ONE_SPECIAL_CHARACTER, AUTH_ERROR_KEY.INCLUSION_ONE_SPECIAL_CHARACTER)
    .matches(REGEXPS.ALLOWED_CHARACTERS, AUTH_ERROR_KEY.DOES_NOT_CONTAIN_FORBIDDEN_CHARACTERS)
    .test({
      test: (val) => val ? !REGEXPS.START_OR_END_WITH_SPACE.test(val) : true,
      message: AUTH_ERROR_KEY.START_OR_END_WITH_SPACE,  
    }),
};
